import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Typography
} from '@mui/material';
import { RestoreFromTrash, Delete, Folder } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import logger from '../../utils/logger';

const RecycleBin = ({ open, onClose, userId, familyCode }) => {
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDeletedItems = useCallback(async () => {
    try {
      setLoading(true);
      const recycleBinRef = collection(db, 'recycleBin');
      const q = query(
        recycleBinRef,
        where('userId', '==', userId),
        where('familyCode', '==', familyCode)
      );
      
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDeletedItems(items);
    } catch (error) {
      logger.error('Error loading recycle bin items:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, familyCode]);

  useEffect(() => {
    if (open) {
      loadDeletedItems();
    }
  }, [open, loadDeletedItems]);

  const handleRestore = async (item) => {
    try {
      // Move the item back to its original collection
      const originalRef = doc(db, item.originalPath);
      await updateDoc(originalRef, {
        ...item.originalData,
        deletedAt: null
      });

      // Remove from recycle bin
      const recycleBinRef = doc(db, 'recycleBin', item.id);
      await deleteDoc(recycleBinRef);

      // Refresh the list
      await loadDeletedItems();
    } catch (error) {
      logger.error('Error restoring item:', error);
    }
  };

  const handlePermanentDelete = async (item) => {
    try {
      const recycleBinRef = doc(db, 'recycleBin', item.id);
      await deleteDoc(recycleBinRef);
      await loadDeletedItems();
    } catch (error) {
      logger.error('Error permanently deleting item:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Recycle Bin</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : deletedItems.length === 0 ? (
          <Typography>No items in recycle bin</Typography>
        ) : (
          <List>
            {deletedItems.map((item) => (
              <ListItem key={item.id}>
                <ListItemIcon>
                  <Folder />
                </ListItemIcon>
                <ListItemText 
                  primary={item.name || 'Unnamed file'} 
                  secondary={`Deleted on ${new Date(item.deletedAt).toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    aria-label="restore" 
                    onClick={() => handleRestore(item)}
                  >
                    <RestoreFromTrash />
                  </IconButton>
                  <IconButton 
                    edge="end" 
                    aria-label="delete permanently" 
                    onClick={() => handlePermanentDelete(item)}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecycleBin; 